import React, { useContext, useState } from 'react'
import { useGetUserImage, useUpdateImageUser, useUserChangePassword } from 'lib/api/user';
import { AuthContext } from 'App';

import { Box, Typography, Button } from "@mui/material"
import { styled } from '@mui/material/styles';
import { BaseResponse, ErrorResponse, IChangePassword } from 'interfaces';
import { AxiosError } from 'axios';
import CircularProgressBox from 'components/utils/CircularProgressBox'
import AvatarDefault from 'components/utils/AvatarDefault'
import ChangeAvatar from './ChangeAvatar';
import ChangePassword from './ChangePassword';
import AlertMessage from 'components/utils/AlertMessage';

const BoxWrapper = styled(Box)({
    display: "flex",
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
    gap: 5,
    alignItems: 'center'
});

const Profile: React.FC = () => {
    const { user, isLoading } = useContext(AuthContext)
    const [step, setStep] = useState<1 | 2 | 3>(1)
    
    const [showMessage, setShowMessage] = useState<{
        open: boolean
        message: string | string[]
        type: "error" | "success" | "info" | "warning"
    }>({
        open: false,
        message: '',
        type: 'success'
    })
    const [imageChange, setImageChange] = useState<string>('')
    const [passwordData, setPasswordData] =
        useState<IChangePassword>({
            password_confirmation: "",
            password: "",
    });

    const { data: imageUser, refetch, isLoading: isLoadingImage, isSuccess } = useGetUserImage(Number(user?.data?.id))

    const handleUpdateImageUser = useUpdateImageUser((res: BaseResponse) => {
        setShowMessage({
            open: true,
            message: res?.message || '',
            type: 'success'
        })
        setStep(1)
        refetch()
        setImageChange('')
    }, (err: AxiosError<ErrorResponse>) => {
        setShowMessage({
            open: true,
            message: err?.response?.data?.errors || 'エーラ',
            type: 'error'
        })
    })
    const handleChangeImage = () => {
        if (!imageChange) {
            setShowMessage({
                open: true,
                message: "イメージを選択してください",
                type: "error",
            });
        } else handleUpdateImageUser.mutate({
            id: user?.data?.id as number,
            imageUrl: imageChange,
        });
    }

    const handleUpdatePassword = useUserChangePassword((res: BaseResponse) => {
        setShowMessage({
            open: true,
            message: res?.message || '',
            type: 'success'
        })
        setPasswordData({
            password: "",
            password_confirmation: "",
        })
        setStep(1)
    }, (err: AxiosError<ErrorResponse>) => {
        setShowMessage({
            open: true,
            message: err?.response?.data?.error?.messages || "エラー",
            type: "error",
        });
    })
    const handleChangePassword = () => {
        handleUpdatePassword.mutate(passwordData);
    }

    return (
        <>
            {
                (isLoading || isLoadingImage) ? (
                    <CircularProgressBox />
                ) : isSuccess && (
                    <>
                        <BoxWrapper sx={{ backgroundColor: '#c1e2f6', marginTop: '2rem', marginBottom: '1rem' }}>
                            <Box display="flex" alignItems="center" sx={{ margin: '2rem 0 2rem 0' }} >
                                <AvatarDefault width={75} height={75} src={imageChange || imageUser?.data?.image} text={user?.data?.nickname?.charAt(0)} />
                                <Box marginLeft={2}>
                                    <Typography variant='inherit' component="div">
                                        <Typography component="span" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>{user?.data?.nickname}</Typography>
                                        <Typography component="span" sx={{ marginLeft: "0.5rem", fontSize: '0.8rem' }}>さん</Typography>
                                    </Typography>
                                </Box>
                            </Box>
                        </BoxWrapper>

                        <BoxWrapper>
                            {step === 1 && (
                                <>  
                                    <Box sx={{ display: 'flex', justifyContent: 'center', margin: "2rem 0 1rem 0" }}>
                                        <Button variant="outlined" onClick={() => setStep(2)}>プロフィールを変更する</Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', margin: "0rem 0 1rem 0" }}>
                                        <Button variant="outlined" onClick={() => setStep(3)}>パスワードを変更する</Button>
                                    </Box>
                                </>
                            )}
                            {
                                step === 2 && (
                                    <ChangeAvatar
                                        setShowMessage={setShowMessage}
                                        setImageChange={setImageChange}
                                        isLoading={isLoading || handleUpdateImageUser.isPending}
                                        handleChangeImage={handleChangeImage}
                                    />
                                )
                            }
                            {
                                step === 3 && (
                                    <ChangePassword
                                        isLoading={handleUpdatePassword.isPending}
                                        passwordData={passwordData}
                                        setPasswordData={setPasswordData}
                                        handleChangePassword={handleChangePassword}
                                    />
                                )
                            }
                        </BoxWrapper>
                    </>
                )
            }
            <AlertMessage
                open={showMessage.open}
                setOpen={(open: boolean) => setShowMessage({ open, message: '', type: 'warning' })}
                severity={showMessage.type}
                message={showMessage.message}
            />
        </>
    )
}

export default Profile